import React from "react";
import { Formik, Form } from "formik";
import Swal from "sweetalert2";

import { CustomSolutionSchema } from "../../../forms/schema";
import { TextInput, CheckboxInput } from "../../../forms/components";
import DataTable from "../../../components/DataTable";
import {
	createSalesChannel,
	updateCustomSolution,
} from "../../../libs/functions";
import Util from "../../../libs/util";

class CustomSolutionModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			formInitialValues: {},
			formState: "create",
			editChannel: null,
		};
	}

	getChannels = () => this.props.retrieveCustomSolution(this.props.index);

	createChannel = () => {
		this.setState(
			{
				formInitialValues: { ...CustomSolutionSchema.initialValues },
				formState: "create",
				editChannel: null,
			},
			() => {
				Util.showModal("create-custom");
			}
		);
	};

	editChannel = (channel) => {
		const initialValues = {
			...CustomSolutionSchema.initialValues,
			name: channel.name,
			adminPin: channel.adminPin,
			callbackUrl: channel.callbackUrl || "",
			redirectUrl: channel.redirectUrl || "",
			eventCallbackUrl: channel.eventCallbackUrl || "",
			stripeTerminalId: channel.stripeTerminalId || "",
			facepayId: channel.facepayId || "",
			isHeadless: Boolean(channel.isHeadless),
			isCardOnly: Boolean(channel.isCardOnly),
			isQrOnly: Boolean(channel.isQrOnly),
			hasEzlink: Boolean(channel.hasEzlink),
			isBankCardPayment: Boolean(channel.isBankCardPayment),
			isRsaSignature: Boolean(channel.isRsaSignature),
		};

		this.setState(
			{
				formInitialValues: initialValues,
				formState: "edit",
				editChannel: channel._id,
			},
			() => {
				Util.showModal("create-custom");
			}
		);
	};

	handleCreate = (values, { setSubmitting, resetForm }) => {
		Swal.fire({
			title: `Creating Customized Solution`,
			onBeforeOpen: () => {
				Util.hideModal("create-custom");
				resetForm();
				Util.showModal("customized-solution");
				Swal.showLoading();
			},
			onOpen: async () => {
				try {
					let channel = await createSalesChannel({
						...values,
						merchantId: this.props.merchant.id,
						type: "custom",
					});
					Swal.hideLoading();

					if (channel) {
						setSubmitting(false);
						Swal.fire({
							icon: "success",
							title: `Customized Solution Created`,
							onClose: this.getChannels,
						});
					}
				} catch (err) {
					console.log(err);
					setSubmitting(false);
					if (err.response) {
						if (err.response.status === 401) {
							Util.expireSession().then(() => {
								window.location = "/login";
							});
						} else {
							Swal.fire({
								icon: "error",
								title: `Unable to Create Customized Solution`,
							});
						}
					}
				}
			},
			onClose: this.getChannels,
		});
	};

	handleEdit = (values, { setSubmitting }) => {
		Swal.fire({
			title: `Editing Customized Solution`,
			onBeforeOpen: () => {
				Swal.showLoading();
				Util.hideModal("create-custom");
			},
			onOpen: async () => {
				try {
					const status = await updateCustomSolution({
						_id: this.state.editChannel,
						...values,
					});

					Swal.hideLoading();

					if (!status) throw new Error("Update failed");

					setSubmitting(false);
					Swal.fire({
						icon: "success",
						title: `Customized Solution Updated`,
						onClose: this.getChannels,
					});
				} catch (err) {
					setSubmitting(false);
					console.log(err);
					Swal.fire({
						icon: "error",
						title: `Unable to update customized solution`,
					});
				}
			},
			onClose: this.getChannels,
		});
	};

	updateChannel = (id, type) => async (evt) => {
		if (this.props.master) {
			try {
				let payload = {
					_id: id,
					[type]: evt.target.checked,
				};

				let status = await updateCustomSolution(payload);

				if (status) {
					Swal.fire({
						icon: "success",
						title: `Customized Solution Updated`,
						onClose: this.getChannels,
					});
				} else {
					Swal.fire({
						icon: "error",
						title: "Unable to update Customized Solution",
					});
				}
			} catch (err) {
				if (err.response) {
					if (err.response.status === 401) {
						Util.expireSession().then(() => {
							window.location = "/login";
						});
					} else {
						Swal.fire({
							icon: "error",
							title: `Unable to update Customized Solution`,
						});
					}
				}
			}
		}
	};

	render() {
		const { id, merchant, customList } = this.props;
		const { formState, formInitialValues } = this.state;

		// Check if sales channel has special flags
		const hasQrOnly = customList.some((c) => c.isQrOnly);
		const hasCardOnly = customList.some((c) => c.isCardOnly);
		const hasHeadless = customList.some((c) => c.isHeadless);
		const hasEzlink = customList.some((c) => c.hasEzlink);
		const hasBankCardPayment = customList.some((c) => c.isBankCardPayment);
		const hasRsaSignature = customList.some((c) => c.isRsaSignature);
		const hasCallbackUrl = customList.some((c) => c.callbackUrl);
		const hasRedirectUrl = customList.some((c) => c.redirectUrl);
		const hasEventCallbackUrl = customList.some((c) => c.eventCallbackUrl);
		const hasStripe = customList.some((c) => c.stripeTerminalId);
		const hasFacepay = customList.some((c) => c.facepayId);

		return (
			<>
				<div id={id} className="uk-modal-container">
					<div className="uk-modal-dialog uk-modal-body">
						<h2 className="uk-modal-title uk-text-capitalize">
							Customized Solution for {merchant ? merchant.name : ""}
							<button
								className="uk-margin-left uk-icon-button uk-button-primary"
								uk-icon="icon: fa-solid-plus-circle; ratio: 0.7"
								type="button"
								onClick={this.createChannel}
							/>
							<button
								className="uk-margin-small-left uk-icon-button uk-button-primary tut-refresh-list"
								uk-icon="icon: fa-solid-sync-alt; ratio: 0.7"
								type="button"
								onClick={this.getChannels}
							/>
						</h2>

						{customList.length > 0 && (
							<DataTable
								ref={this.datatableRef}
								className="uk-table uk-table-hover uk-table-striped uk-table-middle uk-table-responsive"
							>
								<thead>
									<tr>
										<th>Edit</th>
										<th>Name</th>
										<th className="uk-text-center">Admin PIN</th>
										{hasHeadless && (
											<th className="uk-text-center">Headless</th>
										)}
										{hasCardOnly && (
											<th className="uk-text-center">Card Payment Only</th>
										)}
										{hasQrOnly && (
											<th className="uk-text-center">QR Payment Only</th>
										)}
										{hasEzlink && (
											<th className="uk-text-center">Has EZ-Link Payment</th>
										)}
										{hasBankCardPayment && (
											<th className="uk-text-center">Is Bank Card Payment</th>
										)}
										{hasRsaSignature && (
											<th className="uk-text-center">RSA Signature</th>
										)}
										{hasCallbackUrl && (
											<th className="uk-text-center">Callback URL</th>
										)}
										{hasStripe && <th className="uk-text-center">Stripe ID</th>}
										{hasFacepay && (
											<th className="uk-text-center">FacePay ID</th>
										)}
										<th className="uk-text-center">Archive</th>
									</tr>
								</thead>
								<tbody>
									{customList.map((channel, index) => {
										return (
											<tr key={index}>
												<td className="uk-text-center">
													<button
														className="uk-icon-button uk-button-primary"
														uk-icon="icon: fa-solid-pencil-alt; ratio: 0.7"
														type="button"
														onClick={() => this.editChannel(channel)}
													/>
												</td>
												<td>{channel.name}</td>
												<td className="uk-text-center">
													{channel.adminPin || "000000"}
												</td>
												{hasHeadless && (
													<td className="uk-text-center">
														<input
															className="uk-checkbox"
															type="checkbox"
															checked={channel.isHeadless || false}
															onChange={this.updateChannel(
																channel._id,
																"isHeadless"
															)}
														/>
													</td>
												)}
												{hasCardOnly && (
													<td className="uk-text-center">
														<input
															className="uk-checkbox"
															type="checkbox"
															checked={channel.isCardOnly || false}
															onChange={this.updateChannel(
																channel._id,
																"isCardOnly"
															)}
														/>
													</td>
												)}
												{hasQrOnly && (
													<td className="uk-text-center">
														<input
															className="uk-checkbox"
															type="checkbox"
															checked={channel.isQrOnly || false}
															onChange={this.updateChannel(
																channel._id,
																"isQrOnly"
															)}
														/>
													</td>
												)}
												{hasEzlink && (
													<td className="uk-text-center">
														<input
															className="uk-checkbox"
															type="checkbox"
															checked={channel.hasEzlink || false}
															onChange={this.updateChannel(
																channel._id,
																"hasEzlink"
															)}
														/>
													</td>
												)}
												{hasBankCardPayment && (
													<td className="uk-text-center">
														<input
															className="uk-checkbox"
															type="checkbox"
															checked={channel.isBankCardPayment || false}
															onChange={this.updateChannel(
																channel._id,
																"isBankCardPayment"
															)}
														/>
													</td>
												)}
												{hasRsaSignature && (
													<td className="uk-text-center">
														<input
															className="uk-checkbox"
															type="checkbox"
															checked={channel.isRsaSignature || false}
															onChange={this.updateChannel(
																channel._id,
																"isRsaSignature"
															)}
														/>
													</td>
												)}
												{hasCallbackUrl && (
													<td className="uk-text-center uk-text-truncate">
														{channel.callbackUrl}
													</td>
												)}
												{hasRedirectUrl && (
													<td className="uk-text-center uk-text-truncate">
														{channel.redirectUrl}
													</td>
												)}
												{hasEventCallbackUrl && (
													<td className="uk-text-center uk-text-truncate">
														{channel.eventCallbackUrl}
													</td>
												)}
												{hasStripe && (
													<td className="uk-text-center uk-text-truncate">
														{channel.stripeTerminalId}
													</td>
												)}
												{hasFacepay && (
													<td className="uk-text-center uk-text-truncate">
														{channel.facepayId}
													</td>
												)}
												<td>
													<button
														className="uk-button uk-button-small uk-button-primary uk-width-1-1 action-btn"
														type="button"
														onClick={this.props.archive(channel._id)}
													>
														<span className="uk-text-middle uk-text-bold">
															Archive
														</span>
													</button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</DataTable>
						)}
					</div>
				</div>
				<div id="create-custom" uk-modal="bg-close: false">
					<div className="uk-modal-dialog uk-modal-body">
						<h2 className="uk-modal-title uk-text-capitalize">
							{formState} Custom Solution
						</h2>
						<Formik
							initialValues={formInitialValues}
							validationSchema={CustomSolutionSchema.validation}
							enableReinitialize={true}
							onSubmit={
								formState === "create" ? this.handleCreate : this.handleEdit
							}
						>
							{(props) => (
								<Form className="uk-form-stacked" onSubmit={props.handleSubmit}>
									<TextInput
										label="Name"
										id="name"
										name="name"
										type="text"
										placeholder=""
									/>
									<TextInput
										label="Admin PIN"
										id="adminPin"
										name="adminPin"
										type="text"
										placeholder=""
									/>
									<TextInput
										label="Callback URL"
										id="callbackUrl"
										name="callbackUrl"
										type="text"
										placeholder=""
									/>
									<TextInput
										label="Redirect URL"
										id="redirectUrl"
										name="redirectUrl"
										type="text"
										placeholder=""
									/>
									<TextInput
										label="Event Callback URL"
										id="eventCallbackUrl"
										name="eventCallbackUrl"
										type="text"
										placeholder=""
									/>
									<TextInput
										label="Stripe Terminal ID"
										id="stripeTerminalId"
										name="stripeTerminalId"
										type="text"
										placeholder="tmr_..."
									/>
									<TextInput
										label="FacePay ID"
										id="facepayId"
										name="facepayId"
										type="text"
										placeholder=""
									/>
									<CheckboxInput
										name="isHeadless"
										id="isHeadless"
										label="Headless"
									/>
									<CheckboxInput
										name="isCardOnly"
										id="isCardOnly"
										label="Card Payment Only"
									/>
									<CheckboxInput
										name="isQrOnly"
										id="isQrOnly"
										label="QR Payment Only"
									/>
									<CheckboxInput
										name="hasEzlink"
										id="hasEzlink"
										label="Has EZ-Link Payment"
									/>
									<CheckboxInput
										name="isBankCardPayment"
										id="isBankCardPayment"
										label="Is Bank Card Payment"
									/>
									<CheckboxInput
										name="isRsaSignature"
										id="isRsaSignature"
										label="RSA Signature"
									/>
									<p className="uk-text-right">
										<button
											className="uk-button uk-button-default"
											type="button"
											onClick={() => {
												Util.hideModal("create-custom");
												props.resetForm();
												Util.showModal("customized-solution");
											}}
										>
											Cancel
										</button>
										<button
											className="uk-button uk-button-primary"
											type="submit"
										>
											{formState === "create" ? "Create" : "Edit"}
										</button>
									</p>
								</Form>
							)}
						</Formik>
					</div>
				</div>
			</>
		);
	}
}

export default CustomSolutionModal;
